/**
 * Custom Dropdown Component
 * Accessible custom dropdown for contact form
 */

class CustomDropdown {
	constructor(element) {
		this.dropdown = element;
		this.trigger = element.querySelector('[data-dropdown-trigger]');
		this.list = element.querySelector('[data-dropdown-list]');
		this.valueDisplay = element.querySelector('[data-dropdown-value]');
		this.hiddenInput = element.querySelector('input[type="hidden"], select');
		this.options = Array.from(element.querySelectorAll('[data-value]'));
		this.isOpen = false;
		this.selectedIndex = -1;

		this.init();
	}

	init() {
		// Click trigger to toggle
		this.trigger.addEventListener('click', () => this.toggle());

		// Click option to select
		this.options.forEach((option, index) => {
			option.addEventListener('click', () => this.selectOption(index));
		});

		// Keyboard navigation
		this.trigger.addEventListener('keydown', (e) => this.handleKeyDown(e));
		this.list.addEventListener('keydown', (e) => this.handleKeyDown(e));

		// Close on click outside
		document.addEventListener('click', (e) => {
			if (!this.dropdown.contains(e.target)) {
				this.close();
			}
		});

		// Close on escape
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.close();
				this.trigger.focus();
			}
		});

		// Check for query string parameter and preload value
		this.preloadFromQueryString();
	}

	preloadFromQueryString() {
		const urlParams = new URLSearchParams(window.location.search);
		const fieldName = this.hiddenInput.getAttribute('name');
		const queryValue = urlParams.get(fieldName);

		if (queryValue) {
			// Find the option that matches the query string value
			const matchingIndex = this.options.findIndex(
				option => option.getAttribute('data-value') === queryValue
			);

			if (matchingIndex !== -1) {
				this.selectOption(matchingIndex);
			}
		}
	}

	toggle() {
		this.isOpen ? this.close() : this.open();
	}

	open() {
		this.isOpen = true;
		this.dropdown.classList.add('is-open');
		this.trigger.setAttribute('aria-expanded', 'true');
		this.list.style.display = 'block';
	}

	close() {
		this.isOpen = false;
		this.dropdown.classList.remove('is-open');
		this.trigger.setAttribute('aria-expanded', 'false');
		this.list.style.display = 'none';
	}

	selectOption(index) {
		if (index < 0 || index >= this.options.length) return;

		const option = this.options[index];
		const value = option.getAttribute('data-value');
		const text = option.textContent;

		// Update display and hidden input
		this.valueDisplay.textContent = text;
		this.valueDisplay.classList.add('has-value');
		this.hiddenInput.value = value;
		this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
		this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));

		// Update selected state
		this.options.forEach(opt => opt.classList.remove('is-selected'));
		option.classList.add('is-selected');
		option.setAttribute('aria-selected', 'true');

		this.selectedIndex = index;
		this.close();
		this.trigger.focus();
	}

	highlightOption(index) {
		if (index < 0 || index >= this.options.length) return;

		this.options.forEach(opt => opt.classList.remove('is-highlighted'));
		this.options[index].classList.add('is-highlighted');
		this.selectedIndex = index;
		
		// Scroll into view if needed
		this.options[index].scrollIntoView({ block: 'nearest' });
	}

	handleKeyDown(e) {
		if (!this.isOpen) {
			// Open on Enter, Space, or Arrow keys when closed
			if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
				e.preventDefault();
				this.open();
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				this.highlightOption(Math.min(this.selectedIndex + 1, this.options.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				this.highlightOption(Math.max(this.selectedIndex - 1, 0));
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				if (this.selectedIndex >= 0) {
					this.selectOption(this.selectedIndex);
				}
				break;
			case 'Home':
				e.preventDefault();
				this.highlightOption(0);
				break;
			case 'End':
				e.preventDefault();
				this.highlightOption(this.options.length - 1);
				break;
		}
	}
}

// Initialize all custom dropdowns
document.addEventListener('DOMContentLoaded', () => {
	const dropdowns = document.querySelectorAll('[data-dropdown]');
	dropdowns.forEach(dropdown => new CustomDropdown(dropdown));
});

export default CustomDropdown;
